import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login as apiLogin, userCheck } from '../api/user';
import isTokenValid from '../utils/jwtDecode';
import { AuthState, LoginParams, SessionData } from '../types/authTypes';

const initialState: AuthState = {
  isAuthenticated: false,
  loading: false,
  error: null,
  user: null,
  session: null,
  newPassRequired: false,
  isUserApproved: true,
  cognitoId: null,
};

export const validateToken = createAsyncThunk<void, void>('auth/validateToken', async (_, { dispatch }) => {
  const persistedAuthState = localStorage.getItem('auth');

  if (persistedAuthState) {
    const parsedState: SessionData = JSON.parse(persistedAuthState);

    if (isTokenValid(parsedState.signInUserSession.accessToken)) {
      dispatch(
        setAuthState({
          isAuthenticated: true,
          user: parsedState.username,
          session: parsedState.signInUserSession,
          newPassRequired: parsedState.newPassRequired,
          cognitoId: parsedState.cognitoId,
        })
      );
    } else {
      dispatch(logout());
    }
  } else {
    console.error('No persisted session found');
  }
});

export const login = createAsyncThunk<SessionData, LoginParams, { rejectValue: string }>(
  'auth/login',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiLogin(email, password);
      const { signInUserSession, username } = response;

      const { success } = await userCheck(email);
      if (!success) {
        throw new Error('User is not approved.');
      }

      const sessionData: SessionData = {
        signInUserSession: {
          idToken: signInUserSession.idToken,
          accessToken: signInUserSession.accessToken,
          refreshToken: signInUserSession.refreshToken,
        },
        username,
        newPassRequired: response.newPassRequired,
        cognitoId: response.cognitoId,
      };

      localStorage.setItem('auth', JSON.stringify(sessionData));

      dispatch(validateToken());

      return sessionData;
    } catch (err) {
      localStorage.removeItem('auth');
      return rejectWithValue((err as Error).message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.session = null;
      state.newPassRequired = false;
      state.isUserApproved = true;
      state.cognitoId = null;
      localStorage.removeItem('auth');
    },
    setAuthState: (
      state,
      action: PayloadAction<{
        isAuthenticated: boolean;
        user: string | null;
        session: AuthState['session'];
        newPassRequired: boolean;
        cognitoId: string | null;
      }>
    ) => {
      state.isAuthenticated = action.payload.isAuthenticated;
      state.user = action.payload.user;
      state.session = action.payload.session;
      state.newPassRequired = action.payload.newPassRequired;
      state.cognitoId = action.payload.cognitoId;
      state.isUserApproved = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.isUserApproved = true;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<SessionData>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.username;
        state.session = action.payload.signInUserSession;
        state.newPassRequired = action.payload.newPassRequired || false;
        state.isUserApproved = true;
      })
      .addCase(login.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
        if (action.payload === 'User is not approved.') {
          state.isUserApproved = false;
        }
      });
  },
});

export const { logout, setAuthState } = authSlice.actions;
export default authSlice.reducer;
